/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { useCallback, useEffect, useRef, useState } from "react";

import * as React from "react";
import { BodyLarge } from "components/shared/SpectrumCss";
import styled from "styled-components";
import { devices, fontFamilies, useIsMobile } from "components/shared/Styles";
import {
  Analytics,
  AnalyticsEvent,
  ExpressCheckoutButtonPosition,
  PostEnhanceStudioNudgeButtonPosition,
} from "constants/Analytics";
import {
  CompletedEnhanceSpeechTrack,
  EnhanceSpeechTrackErrorType,
} from "../Utilities";
import { SampleBasicEnhancedTrackPlayer } from "components/EnhancedSpeech/SampleBasicEnhancedTrackPlayer";
import premiumVideoFade from "images/EnhanceSpeech/premium-video-fade.png";
import premiumVideoFade2x from "images/EnhanceSpeech/premium-video-fade@2x.png";
import premiumPreviewDesktop from "images/EnhanceSpeech/new-video-upsell-content.jpg";
import premiumPreviewDesktop2x from "images/EnhanceSpeech/new-video-upsell-content@2x.jpg";
import { UpgradeExpressButton } from "components/PurchaseExpress";
import { ButtonIcon } from "components/shared/ButtonIcon";
import upgradeIcon from "components/shared/images/upgrade-icon.svg";
import adjustStrengthImage1x from "./images/upsells/adjust-strength@1x.png";
import adjustStrengthImage2x from "./images/upsells/adjust-strength@2x.png";
import bulkUploadImage1x from "./images/upsells/bulk-upload@1x.png";
import bulkUploadImage2x from "./images/upsells/bulk-upload@2x.png";
import enhanceMoreImage1x from "./images/upsells/enhance-more@1x.png";
import enhanceMoreImage2x from "./images/upsells/enhance-more@2x.png";
import videoImage1x from "./images/upsells/video@1x.png";
import videoImage2x from "./images/upsells/video@2x.png";
import addMusicIcon1x from "./images/upsells/add-music@1x.png";
import addMusicIcon2x from "./images/upsells/add-music@2x.png";
import getTranscriptIcon1x from "./images/upsells/get-transcript@1x.png";
import getTranscriptIcon2x from "./images/upsells/get-transcript@2x.png";
import makeEditsIcon1x from "./images/upsells/make-edits@1x.png";
import makeEditsIcon2x from "./images/upsells/make-edits@2x.png";
import chevronRight1x from "./images/upsells/chevron-right@1x.png";
import chevronRight2x from "./images/upsells/chevron-right@2x.png";
import { useDispatch } from "react-redux";
import { useCreateProject } from "hooks/useCreateProject";
import { v4 as uuidv4 } from "uuid";
import { insertFileAtPlayhead } from "actions/ClipActions";
import { transcribeClip } from "actions/Clips/TranscribeActions";
import { addToast } from "actions/ToastMessageActions";
import { useFileFromRefreshUrl } from "hooks/useFileFromRefreshUrl";
import { useCurrentUser } from "actions/CurrentUserActions";
import "stylesheets/spectrum.scss";

const UpsellContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 0;
  width: 100%;

  ${devices.small} {
    text-align: left;
    border-radius: 32px;
    padding: 0;
    background-color: none;

    margin: 16px 0 0 0;
  }
`;

const ErrorImage = styled.img`
  max-width: 390px;
  width: 100%;
  height: 100%;
  box-shadow: 0px 12px 24px 0px rgba(57, 63, 199, 0.09),
    0px 0px 2px 0px rgba(0, 2, 56, 0.15), 0px 3px 4px 0px rgba(0, 2, 56, 0.03);
  border-radius: 16px;

  ${devices.medium} {
    max-width: 348px;
  }

  ${devices.small} {
    max-width: 100%;
  }
`;

const EnhancingUpsellImage = styled.img`
  width: 278px;
  height: 125px; //Prevents popping in when loading"
`;

const FinishedUpsellImage = styled.img`
  width: 448px;
  height: 248px; //Prevents popping in when loading"

  ${devices.medium} {
    width: 248px;
    height: 137px;
  }

  ${devices.small} {
    display: none;
  }
`;

const UpsellContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  padding: 50px 50px 50px 56px;

  ${devices.medium} {
    padding: 12px;
  }

  ${devices.small} {
    padding: 24px;
    text-align: left;
  }
`;

const UpsellText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UpsellContentFixedSize = styled(UpsellContent)`
  ${devices.small} {
    min-height: 276px;
  }
`;

const UpsellHeading = styled.h3`
  font: normal normal 900 28px/35px ${fontFamilies};
  color: var(--spectrum-global-color-static-gray-900);

  ${devices.small || devices.medium} {
    font: normal normal 900 22px/27px ${fontFamilies};
  }
`;

const Heading = styled.h1.attrs({
  className: "spectrum-Heading spectrum-Heading--sizeXXL",
})`
  font-size: 60px;
  line-height: 68px;
  letter-spacing: 0px;
  text-align: left;
  color: var(--spectrum-global-color-gray-900);

  ${devices.medium} {
    font-size: 46px;
    line-height: 54px;
  }
  ${devices.small} {
    font-size: 34px;
    line-height: 42px;
  }
`;

const UpsellDescription = styled(BodyLarge)`
  font: normal normal normal 18px/22px ${fontFamilies};
  line-height: 22px;
  text-align: left;
  color: var(--spectrum-global-color-gray-900);
  ${devices.small} {
    width: 100%;
  }
`;

const ExampleText = styled(BodyLarge)`
  font-size: 18px;
  line-height: 22px;
  color: var(--spectrum-global-color-gray-900);
  text-align: left;
  ${devices.small} {
    align-self: flex-start;
  }
`;

const InitialContentWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 72px 0 72px 72px;
  gap: 16px;

  ${devices.medium} {
    padding: 0;
  }

  ${devices.small} {
    padding: 0;
    width: calc(100vw - 48px);
    justify-content: flex-start;
    align-items: start;
  }
`;

export const InitialContent: React.FC = () => {
  return (
    <InitialContentWrapper>
      <Heading>{I18n.t("enhance_speech.heading")}</Heading>
      <UpsellDescription>
        {I18n.t("enhance_speech.enhance_speech_description")}
      </UpsellDescription>
      <ExampleText>{I18n.t("enhance_speech.example")}:</ExampleText>
      <SampleBasicEnhancedTrackPlayer />
    </InitialContentWrapper>
  );
};

export const UpsellButton: React.FC<{
  position: ExpressCheckoutButtonPosition;
  enhanceTrackErrorType?: EnhanceSpeechTrackErrorType;
  style?: React.CSSProperties;
}> = ({ position, style, enhanceTrackErrorType }) => {
  return (
    // TODO: Do we still need to track enhanceTrackErrorType? Used to be an unmapped field on UPSELL_EXPLORE_FEATURES...
    <UpgradeExpressButton
      color={"black"}
      position={position}
      buttonStyle={style}
      buttonSize="xl"
    >
      <ButtonIcon alt="" src={upgradeIcon} slot="icon" />
      {I18n.t("plans.premium.button")}
    </UpgradeExpressButton>
  );
};

interface EnhancingUpsells {
  translationKey: string;
  image1x: string;
  image2x: string;
}

const enhancingUpsells: EnhancingUpsells[] = [
  {
    translationKey: "adjust_strength",
    image1x: adjustStrengthImage1x,
    image2x: adjustStrengthImage2x,
  },
  {
    translationKey: "enhance_video",
    image1x: videoImage1x,
    image2x: videoImage2x,
  },
  {
    translationKey: "bulk_upload",
    image1x: bulkUploadImage1x,
    image2x: bulkUploadImage2x,
  },
  {
    translationKey: "enhance_more",
    image1x: enhanceMoreImage1x,
    image2x: enhanceMoreImage2x,
  },
];

const EnhanceFinishedUpsell: React.FC = () => {
  return (
    <UpsellContent>
      <FinishedUpsellImage
        srcSet={`${premiumVideoFade} 1x, ${premiumVideoFade2x} 2x`}
      />
      <UpsellHeading>{I18n.t("enhance_speech.upsell.title")}</UpsellHeading>
      <UpsellDescription>
        {I18n.t("enhance_speech.upsell.description")}
      </UpsellDescription>
      <UpsellDescription>
        {I18n.t("enhance_speech.upsell.trial")}
      </UpsellDescription>
      <UpsellButton
        position={ExpressCheckoutButtonPosition.ENHANCE_FINISHED_UPSELL}
        style={{
          marginTop: 4,
          width: "fit-content",
        }}
      />
    </UpsellContent>
  );
};

const StudioNudge = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 24px;
  width: 100%;
`;

const NudgeHeading = styled.h3`
  font: normal normal 800 22px/28px ${fontFamilies};
  color: var(--spectrum-global-color-static-gray-900);
`;

const NudgeButtons = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;

const NudgeButton = styled.button<{ selected?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font: normal normal 700 16px/20px ${fontFamilies};
  border-radius: 24px;
  max-width: 450px;
  width: 100%;
  height: 72px;
  border: none;
  background-color: var(--spectrum-global-color-static-white);
  box-shadow: var(--phonos-box-shadow);
  padding: 8px;

  &:disabled {
    opacity: ${({ selected }): number => (selected ? 1 : 0.5)};
    color: initial;
  }
`;

const NudgeButtonIcon = styled.img`
  width: 56px;
  height: 56px;
`;

const ChevronRightIcon = styled.img.attrs({
  srcSet: `${chevronRight1x} 1x, ${chevronRight2x} 2x`,
})`
  margin-right: 16px;
`;

const NudgeButtonLabel = styled.div`
  font: normal normal 700 18px/23px ${fontFamilies};
  flex-grow: 1;
  text-align: left;
`;

export const EnhanceFinishedStudioNudge: React.FC<{
  completedEnhanceSpeechTrack?: CompletedEnhanceSpeechTrack;
}> = ({ completedEnhanceSpeechTrack }) => {
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { createProject } = useCreateProject();
  const { getFileFromRefreshUrl } = useFileFromRefreshUrl();
  const [selectedButtonIndex, setSelectedButtonIndex] = useState<number | null>(
    null
  );

  const onNudgeButtonClick = useCallback(
    async (buttonPosition: PostEnhanceStudioNudgeButtonPosition) => {
      try {
        setIsBusy(true);

        Analytics.instance.logEvent(
          AnalyticsEvent.USER_CLICKED_ENHANCE_STUDIO_NUDGE,
          {
            position: buttonPosition,
          }
        );

        if (!completedEnhanceSpeechTrack) {
          throw new Error("Completed enhanced track was null.");
        }

        const enhancedAudioFile = await getFileFromRefreshUrl(
          completedEnhanceSpeechTrack.enhanceSpeechAudioUrl,
          completedEnhanceSpeechTrack.trackName
        );

        if (!enhancedAudioFile) {
          throw new Error("Failed to get enhanced audio file");
        }

        if (await createProject()) {
          const newClipId = uuidv4();
          await dispatch(
            insertFileAtPlayhead({
              file: enhancedAudioFile,
              clipId: newClipId,
            })
          );

          dispatch(transcribeClip({ clipId: newClipId }));
        } else {
          throw new Error("Failed to create project");
        }
      } catch (error) {
        console.error(error);
        dispatch(addToast({ message: I18n.t("home.new_project_error") }));
        newrelic.noticeError(error);
      } finally {
        setIsBusy(false);
        setSelectedButtonIndex(null);
      }
    },
    [
      completedEnhanceSpeechTrack,
      createProject,
      dispatch,
      getFileFromRefreshUrl,
    ]
  );

  const buttonData = [
    {
      icon1x: getTranscriptIcon1x,
      icon2x: getTranscriptIcon2x,
      label: I18n.t("enhance_speech.studio_nudge.get_transcript"),
      buttonPosition:
        PostEnhanceStudioNudgeButtonPosition.GET_TRANSCRIPT_STUDIO_NUDGE,
    },
    {
      icon1x: makeEditsIcon1x,
      icon2x: makeEditsIcon2x,
      label: I18n.t("enhance_speech.studio_nudge.make_edits"),
      buttonPosition:
        PostEnhanceStudioNudgeButtonPosition.MAKE_EDITS_STUDIO_NUDGE,
    },
    {
      icon1x: addMusicIcon1x,
      icon2x: addMusicIcon2x,
      label: I18n.t("enhance_speech.studio_nudge.add_music"),
      buttonPosition:
        PostEnhanceStudioNudgeButtonPosition.ADD_MUSIC_STUDIO_NUDGE,
    },
  ];

  const nudgeButtons = buttonData.map((button, index) => (
    <NudgeButton
      key={index}
      onClick={(): void => {
        setSelectedButtonIndex(index);
        onNudgeButtonClick(button.buttonPosition);
      }}
      disabled={isBusy}
      selected={selectedButtonIndex === index}
    >
      <NudgeButtonIcon srcSet={`${button.icon1x} 1x, ${button.icon2x} 2x`} />
      <NudgeButtonLabel>{button.label}</NudgeButtonLabel>
      {isBusy && selectedButtonIndex === index ? (
        <div style={{ paddingRight: "20px", height: "16px", width: "16px" }}>
          <sp-progress-circle size="s" indeterminate={true} />
        </div>
      ) : (
        <ChevronRightIcon />
      )}
    </NudgeButton>
  ));

  return (
    <StudioNudge data-testid="studio-nudge">
      <NudgeHeading>
        {I18n.t("enhance_speech.studio_nudge.header")}
      </NudgeHeading>
      <NudgeButtons data-testid="studio-nudge-buttons">
        {nudgeButtons}
      </NudgeButtons>
    </StudioNudge>
  );
};

const FADE_DURATION_MS = 300;
const UPSELL_DURATION_MS = 7000;

const EnhancingUpsell: React.FC = () => {
  const [fadeOut, setFadeOut] = useState(true); // Start faded out so that the initial upsell fades in
  const [upsellIdx, setUpsellIdx] = useState(0);
  const upsell = enhancingUpsells[upsellIdx];
  const preloadedImages = useRef<HTMLImageElement[]>([]);

  // Preload images into a ref to prevent flicker when switching between upsells
  useEffect(() => {
    preloadedImages.current = enhancingUpsells.map((upsell) => {
      const image = new Image();
      image.srcset = `${upsell.image1x} 1x, ${upsell.image2x} 2x`;
      return image;
    });
  }, []);

  useEffect(() => {
    let mounted = true;

    // Fade in the initial upsell
    setTimeout((): void => {
      if (mounted) {
        setFadeOut(false);
      }
    }, 500);

    const timer = setInterval(() => {
      setFadeOut(true);
      setTimeout(() => {
        if (mounted) {
          setUpsellIdx(
            (previousUpsellIdx) =>
              (previousUpsellIdx + 1) % enhancingUpsells.length
          );

          setFadeOut(false);
        }
      }, FADE_DURATION_MS);
    }, UPSELL_DURATION_MS);

    return (): void => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  return (
    <div
      style={{
        opacity: fadeOut ? 0 : 1,
        transition: `opacity ${FADE_DURATION_MS}ms`,
      }}
    >
      <UpsellContentFixedSize>
        <EnhancingUpsellImage
          srcSet={`${upsell.image1x} 1x, ${upsell.image2x} 2x`}
        />
        <UpsellText>
          <UpsellHeading>
            {I18n.t(`enhance_speech.upsells.${upsell.translationKey}.title`)}
          </UpsellHeading>
          <UpsellDescription>
            {I18n.t(
              `enhance_speech.upsells.${upsell.translationKey}.description`
            )}
          </UpsellDescription>
        </UpsellText>
      </UpsellContentFixedSize>
    </div>
  );
};

export const EnhanceUpsellContent: React.FC<{
  isBusy: boolean;
  hasTrackError?: boolean;
  completedEnhanceSpeechTrack?: CompletedEnhanceSpeechTrack;
}> = ({ isBusy, hasTrackError, completedEnhanceSpeechTrack }) => {
  const isMobile = useIsMobile();
  const { currentUser } = useCurrentUser();

  const isPostEnhanceStudioNudgeEnabled = Boolean(
    currentUser?.postEnhanceStudioNudgeEnabled
  );

  const studioNudgeDisabled = isMobile || !isPostEnhanceStudioNudgeEnabled;

  let upsellContent: JSX.Element;

  if (isBusy) {
    upsellContent = <EnhancingUpsell />;
  } else {
    upsellContent = studioNudgeDisabled ? (
      <EnhanceFinishedUpsell />
    ) : (
      <EnhanceFinishedStudioNudge
        completedEnhanceSpeechTrack={completedEnhanceSpeechTrack}
      />
    );
  }

  const errorImage = (
    <ErrorImage
      srcSet={`${premiumPreviewDesktop} 1x, ${premiumPreviewDesktop2x} 2x`}
    />
  );
  return (
    <UpsellContentWrapper>
      {hasTrackError ? errorImage : upsellContent}
    </UpsellContentWrapper>
  );
};
